import React, { useContext, useEffect, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import GatsbyImage from 'gatsby-image'
import useShopifyProduct from '../../hooks/useShopifyProduct'
import useReplaceLineItems from '../../hooks/useReplaceLineItems'
import { LayoutContext, useUI } from '../../contexts'
import Price from '../Price'
import { getBundleDiscountAmount } from '../CurrencyConverterSwitcher'
import { useShopify } from '../../contexts/shopify'
import { useGlobal } from '../../hooks/useGlobal'

const Bundle = ({
  productBundle: {
    title,
    description,
    label,
    addToCartText,
    originalPrice,
    discountedPrice,
    products,
    productImages,
  },
}) => {
  //---------- CONTEXT --------
  const { node_locale, currency: localCurrency } = useContext(LayoutContext)
  const { setCartOpen } = useUI()
  const { sizeVariants } = useShopify()
  const {
    product: { addToCartLoading },
  } = useGlobal(['product'], node_locale)

  //---------- SELECTORS --------
  // session storage sizes
  const [replaceLineItems, { loading }] = useReplaceLineItems()
  const mappedProducts = products.map(
    ({
      shopifyProduct: { handle },
      gender: { name: gender },
      productType: { name: productType },
    }) => ({
      shopifyProduct: useShopifyProduct(handle),
      gender,
      productType,
    }),
  )
  // buy 2 save €5
  const discount = getBundleDiscountAmount(node_locale, products.length)

  const [presentmentPrices, setPresentmentPrices] = useState([])
  const [currencySymbol, setCurrencySymbol] = useState('€')

  useEffect(
    () => {
      if (
        mappedProducts.every(
          ({ shopifyProduct: { product } }) =>
            product != null && product != undefined,
        )
      ) {
        setPresentmentPrices(
          mappedProducts.map(
            ({
              shopifyProduct: {
                product: {
                  variants: {
                    edges: [
                      {
                        node: { presentmentPrices },
                      },
                    ],
                  },
                },
              },
            }) => presentmentPrices,
          ),
        )
      }
    },
    mappedProducts.map(({ shopifyProduct: { product } }) => product),
  )

  useEffect(() => {
    if (localCurrency) {
      setCurrencySymbol(localCurrency.symbol)
    }
  }, [localCurrency])

  const addProductsToCart = async (mappedProducts) => {
    await replaceLineItems(
      mappedProducts.map(
        ({
          shopifyProduct: {
            product: {
              variants: { edges },
            },
          },
          gender,
          productType,
        }) => ({
          variantId:
            edges[
              sizeVariants[productType]
                ? sizeVariants[productType][gender] || 0
                : 0
            ].node.id,
          quantity: 1,
        }),
      ),
    )
    setCartOpen(true)
  }

  return (
    <StyledBundle>
      <Label>
        {label}{' '}
        {node_locale === 'de' || node_locale === 'fr'
          ? `${discount} ${currencySymbol}`
          : `${currencySymbol}${discount}`}
      </Label>
      <ImageWrapper>
        {productImages.map(({ fluid }, index) => (
          <StyledImage
            fluid={fluid}
            alt={`bundle product - ${index}`}
            index={index}
            quantity={productImages.length}
            key={index}
          />
        ))}
      </ImageWrapper>
      <Information>
        <Title>{title}</Title>
        <Details>{description}</Details>
        <AddToCart onClick={() => addProductsToCart(mappedProducts)}>
          <Text load={loading}>
            {addToCartText}
            {` - `}
            <OriginalPrice>
              <Price presentmentPrices={presentmentPrices} />
            </OriginalPrice>
            <DiscountedPrice>
              <Price
                presentmentPrices={presentmentPrices}
                discount={discount}
              />
            </DiscountedPrice>
          </Text>
          <TextLoading load={loading}>
            <span>{addToCartLoading}</span>
          </TextLoading>
        </AddToCart>
      </Information>
    </StyledBundle>
  )
}

const StyledBundle = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  background: ${(props) => props.theme.colors.lightGrey};
  max-width: 100%;
  padding: ${(props) => props.theme.space.marginMobile}vw
    ${(props) => props.theme.space.marginMobile * 1.5}vw;
  margin-bottom: ${(props) => props.theme.space.marginMobile * 2}vw;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    background: #fff;
    max-width: 22.9vw;
    padding: ${(props) => props.theme.space.marginDesktop * 2.3}vw
      ${(props) => props.theme.space.marginDesktop * 3}vw;
    margin-bottom: ${(props) => props.theme.space.marginDesktop * 3}vw;
  }
`

const Label = styled.div`
  position: absolute;
  color: ${(props) => props.theme.colors.blue};
  border: 1px solid ${(props) => props.theme.colors.blue};
  border-radius: 25px;
  top: 1.5vw;
  right: 1.5vw;
  font-size: ${(props) => props.theme.fontSize.m20};
  letter-spacing: 0.05em;
  padding: 1.8vw;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    top: 0.5vw;
    right: 0.5vw;
    font-size: ${(props) => props.theme.fontSize.d12};
    padding: 0.5vw;
  }
`

const ImageWrapper = styled.div`
  position: relative;
  width: 25vw;
  height: 31.7vw;
  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    width: 7.5vw;
    height: 9.5vw;
  }
`

const StyledImage = styled(GatsbyImage)`
  object-fit: cover;
  height: 100%;
  width: 100%;
  position: absolute !important;
  top: 0;
  left: ${({ quantity, index }) =>
    quantity === 1 ? 0 : quantity === 2 ? index * 12 - 6 : index * 9 - 9}vw;
  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    left: ${({ quantity, index }) =>
      quantity === 1 ? 0 : quantity === 2 ? index * 3 - 1.5 : index * 2 - 2}vw;
  }
`

const Information = styled.div`
  flex: 1;
  margin-left: ${(props) => props.theme.space.marginMobile * 1.5}vw;
  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    margin-left: ${(props) => props.theme.space.marginDesktop * 2}vw;
  }
`

const Title = styled.p`
  text-transform: uppercase;
  font-weight: bold;
  font-size: ${(props) => props.theme.fontSize.m23};
  letter-spacing: 0.1em;
  margin-bottom: 1.9vw;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    font-size: ${(props) => props.theme.fontSize.d12};
    letter-spacing: 0.12em;
    margin-bottom: 0.7vw;
  }
`

const Details = styled.p`
  /* font-size: ${(props) => props.theme.fontSize.m26}; */
  font-size: ${(props) => props.theme.fontSize.m23};
  letter-spacing: 0.05em;
  line-height: 1.5;
  margin-bottom: 3.5vw;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    /* font-size: ${(props) => props.theme.fontSize.d14}; */
    font-size: ${(props) => props.theme.fontSize.d12};
    margin-bottom: 0.9vw;
  }
`

const AddToCart = styled.button`
  width: 100%;
  color: ${(props) => props.theme.colors.blue};
  background: white;
  border: 1px ${(props) => props.theme.colors.blue} solid;
  cursor: pointer;
  font-size: ${(props) => props.theme.fontSize.m23};
  padding: ${(props) => props.theme.space.marginMobile * 1}vw 0;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    font-size: ${(props) => props.theme.fontSize.d12};
    padding: ${(props) => props.theme.space.marginDesktop * 1.8}vw 0;
  }
`

const Text = styled.div`
  display: ${(props) => (props.load ? 'none' : 'inline-block')};
  position: relative;

  span {
    font-family: ${(props) => props.theme.fontFamily.medium};
  }
`

const strikethroughAnim = keyframes`
  from {
    transform: scaleX(0) translateY(-50%);
  }
  to {
    transform: scaleX(1) translateY(-50%);
  }
`

const OriginalPrice = styled.div`
  display: inline-block;
  position: relative;

  &::after {
    content: '';
    background: ${(props) => props.theme.colors.blue};
    height: 2px;
    width: 110%;
    position: absolute;
    top: 50%;
    left: -5%;
    transform-origin: left center;
    transform: scaleX(0) translateY(-50%);
    animation: ${strikethroughAnim} 0.8s 0.2s
      ${(props) => props.theme.easeOut.expo} forwards;
  }
`

const fadeIn = keyframes`
  from {opacity: 0;}
  to {opacity: 1;}
`

const DiscountedPrice = styled.div`
  text-align: right;
  position: absolute;
  top: -3vw;
  right: 0;
  opacity: 0;
  animation: ${fadeIn} 0.8s 0.6s ${(props) => props.theme.easeOut.expo} forwards;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    top: -0.9vw;
  }
`

const TextLoading = styled.div`
  display: ${(props) => (props.load ? 'inline-block' : 'none')};

  span {
    font-family: ${(props) => props.theme.fontFamily.medium};
  }
`

export default Bundle
